import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Users, Search, Pencil, Receipt, ShoppingCart, CreditCard, Mail, Phone, MapPin, User } from "lucide-react";
import { useClients, useUpdateClient } from "../api/clients";
import { useClientLedger } from "../api/payments";
import { PlaceOrderModal } from "./Orders";
import { RecordPaymentModal } from "./Payments";
import { ClientProfile } from "../types/models";
import { PageHeader } from "../components/ui/PageHeader";
import { Pagination } from "../components/ui/Pagination";
import { StatusBadge, ActiveBadge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { Button, TextField, TextareaField } from "../components/ui/Form";
import { LoadingState, ErrorState, EmptyState } from "../components/ui/States";
import { toast } from "../components/ui/Toast";
import { getErrorMessage } from "../lib/errors";
import { formatCurrency, formatDateTime, toNumber } from "../utils/helpers";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

export const Clients: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput, 350);
  const [editing, setEditing] = useState<ClientProfile | null>(null);
  const [detail, setDetail] = useState<ClientProfile | null>(null);

  const { data, isLoading, isError, error, refetch, isFetching } = useClients({
    page,
    limit: 10,
    search: search || undefined,
  });

  return (
    <div className="space-y-6">
      <PageHeader title="Clients" subtitle="Dealer accounts and receivable balances." />

      <div className="card flex p-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
            placeholder="Search by company, contact or email…"
            className="input pl-10"
          />
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : data && data.items.length === 0 ? (
          <EmptyState icon={<Users size={32} />} title="No clients found" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700 text-left text-xs uppercase tracking-wide text-slate-400">
                    <th className="px-4 py-3">Company</th>
                    <th className="px-4 py-3">Contact</th>
                    <th className="px-4 py-3 text-right">Ordered</th>
                    <th className="px-4 py-3 text-right">Paid</th>
                    <th className="px-4 py-3 text-right">Balance</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className={isFetching ? "opacity-60 transition-opacity" : ""}>
                  {data?.items.map((c) => {
                    const balance = c.remaining ?? toNumber(c.totalOrdered) - toNumber(c.totalPaid);
                    return (
                      <tr key={c.id} onClick={() => setDetail(c)} className="cursor-pointer border-b border-slate-800 last:border-0 hover:bg-slate-800/40">
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-100">{c.companyName}</div>
                          <div className="text-xs text-slate-500">{c.city ?? ""}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-300">
                          <div>{c.user?.fullName ?? c.contactPerson ?? "—"}</div>
                          <div className="text-xs text-slate-500">{c.user?.email}</div>
                        </td>
                        <td className="px-4 py-3 text-right text-slate-300">{formatCurrency(c.totalOrdered)}</td>
                        <td className="px-4 py-3 text-right text-slate-300">{formatCurrency(c.totalPaid)}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={toNumber(balance) > 0 ? "font-semibold text-red-400" : "text-emerald-400"}>
                            {formatCurrency(balance)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <ActiveBadge active={c.isActive} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={(e) => { e.stopPropagation(); setDetail(c); }} className="rounded-lg p-2 text-slate-400 hover:bg-slate-700 hover:text-slate-100" title="View details">
                              <Receipt size={16} />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setEditing(c); }} className="rounded-lg p-2 text-slate-400 hover:bg-slate-700 hover:text-slate-100" title="Edit">
                              <Pencil size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination meta={data?.meta} onPageChange={setPage} />
          </>
        )}
      </div>

      {editing && <EditClientModal client={editing} onClose={() => setEditing(null)} />}
      {detail && <ClientDetailModal client={detail} onClose={() => setDetail(null)} />}
    </div>
  );
};

const EditClientModal: React.FC<{ client: ClientProfile; onClose: () => void }> = ({ client, onClose }) => {
  const update = useUpdateClient();
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      companyName: client.companyName,
      contactPerson: client.contactPerson ?? "",
      phone: client.phone ?? "",
      address: client.address ?? "",
      city: client.city ?? "",
      taxNumber: client.taxNumber ?? "",
      isActive: client.isActive,
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await update.mutateAsync({
        id: client.id,
        input: {
          companyName: values.companyName,
          contactPerson: values.contactPerson || undefined,
          phone: values.phone || undefined,
          address: values.address || undefined,
          city: values.city || undefined,
          taxNumber: values.taxNumber || undefined,
          isActive: values.isActive,
        },
      });
      toast.success("Client updated");
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Edit Client"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={onSubmit} loading={update.isPending}>Save changes</Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField label="Company" required error={errors.companyName?.message as string} {...register("companyName", { required: "Required" })} />
        <TextField label="Contact Person" {...register("contactPerson")} />
        <TextField label="Phone" {...register("phone")} />
        <TextField label="City" {...register("city")} />
        <TextField label="Tax Number" {...register("taxNumber")} />
        <label className="flex items-center gap-2 self-end pb-2 text-sm text-slate-300">
          <input type="checkbox" className="h-4 w-4 rounded border-slate-600 bg-slate-700" {...register("isActive")} />
          Active account
        </label>
        <TextareaField label="Address" className="sm:col-span-2" {...register("address")} />
      </form>
    </Modal>
  );
};

const ClientDetailModal: React.FC<{ client: ClientProfile; onClose: () => void }> = ({ client, onClose }) => {
  const [tab, setTab] = useState<"orders" | "payments">("orders");
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [recordingPayment, setRecordingPayment] = useState(false);
  const { data, isLoading, isError, error, refetch } = useClientLedger(client.id);

  const contactName = client.user?.fullName ?? client.contactPerson;
  const location = [client.address, client.city].filter(Boolean).join(", ");
  const lockedClient = { id: client.id, companyName: client.companyName };

  return (
    <>
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={client.companyName}
      description="Client details, orders and payments"
      size="xl"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div className="flex gap-2">
            <Button icon={<ShoppingCart size={16} />} onClick={() => setCreatingOrder(true)}>New Order</Button>
            <Button variant="secondary" icon={<CreditCard size={16} />} onClick={() => setRecordingPayment(true)}>Record Payment</Button>
          </div>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Details */}
        <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-200">Details</h4>
            <ActiveBadge active={client.isActive} />
          </div>
          <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
            {contactName && <DetailItem icon={<User size={15} />} value={contactName} />}
            {client.user?.email && <DetailItem icon={<Mail size={15} />} value={client.user.email} />}
            {(client.phone || client.user?.phone) && <DetailItem icon={<Phone size={15} />} value={(client.phone ?? client.user?.phone) as string} />}
            {location && <DetailItem icon={<MapPin size={15} />} value={location} />}
            {client.taxNumber && <DetailItem icon={<Receipt size={15} />} value={`Tax: ${client.taxNumber}`} />}
          </div>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : data ? (
          <>
            <div className="grid grid-cols-3 gap-3">
              <Summary label="Ordered" value={formatCurrency(data.summary.totalOrdered)} />
              <Summary label="Paid" value={formatCurrency(data.summary.totalPaid)} />
              <Summary label="Balance" value={formatCurrency(data.summary.remaining)} accent={toNumber(data.summary.remaining) > 0 ? "text-red-400" : "text-emerald-400"} />
            </div>

            {/* Tabs */}
            <div className="flex gap-1 rounded-lg bg-slate-900/60 p-1">
              <TabButton active={tab === "orders"} onClick={() => setTab("orders")} icon={<ShoppingCart size={15} />} label={`Orders (${data.orders.length})`} />
              <TabButton active={tab === "payments"} onClick={() => setTab("payments")} icon={<CreditCard size={15} />} label={`Payments (${data.payments.length})`} />
            </div>

            {tab === "orders" ? (
              <LedgerTable
                cols={["Order", "Status", "Placed", "Total"]}
                rows={data.orders.map((o) => ({
                  id: o.id,
                  cells: [o.orderNumber, <StatusBadge status={o.status} />, formatDateTime(o.placedAt), formatCurrency(o.totalAmount)],
                }))}
                empty="No orders yet."
              />
            ) : (
              <LedgerTable
                cols={["Payment", "Method", "Received", "Amount"]}
                rows={data.payments.map((p) => ({
                  id: p.id,
                  cells: [p.paymentNumber, <StatusBadge status={p.method} />, formatDateTime(p.paidAt), formatCurrency(p.amount)],
                }))}
                empty="No payments yet."
              />
            )}
          </>
        ) : null}
      </div>
    </Modal>

    {creatingOrder && (
      <PlaceOrderModal isManagement lockedClient={lockedClient} onClose={() => setCreatingOrder(false)} />
    )}
    {recordingPayment && (
      <RecordPaymentModal lockedClient={lockedClient} onClose={() => setRecordingPayment(false)} />
    )}
    </>
  );
};

const DetailItem: React.FC<{ icon: React.ReactNode; value: string }> = ({ icon, value }) => (
  <div className="flex items-center gap-2 text-sm text-slate-300">
    <span className="text-slate-500">{icon}</span>
    <span className="truncate">{value}</span>
  </div>
);

const TabButton: React.FC<{ active: boolean; onClick: () => void; icon: React.ReactNode; label: string }> = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
      active ? "bg-slate-700 text-slate-100" : "text-slate-400 hover:text-slate-200"
    }`}
  >
    {icon}
    {label}
  </button>
);

const Summary: React.FC<{ label: string; value: string; accent?: string }> = ({ label, value, accent }) => (
  <div className="rounded-lg bg-slate-900/40 p-3 text-center">
    <div className="text-xs text-slate-400">{label}</div>
    <div className={`mt-1 text-lg font-bold ${accent ?? "text-slate-100"}`}>{value}</div>
  </div>
);

const LedgerTable: React.FC<{
  cols: string[];
  rows: { id: string; cells: React.ReactNode[] }[];
  empty: string;
}> = ({ cols, rows, empty }) => {
  if (rows.length === 0) {
    return <p className="rounded-lg border border-slate-700 bg-slate-900/30 px-3 py-6 text-center text-sm text-slate-500">{empty}</p>;
  }
  return (
    <div className="overflow-hidden rounded-lg border border-slate-700">
      <div className="max-h-80 overflow-y-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-700 bg-slate-900/40 text-left text-xs uppercase tracking-wide text-slate-400">
              {cols.map((c, i) => (
                <th key={c} className={`px-3 py-2 ${i === cols.length - 1 ? "text-right" : ""}`}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-800 last:border-0">
                {r.cells.map((cell, i) => (
                  <td key={i} className={`px-3 py-2 ${i === r.cells.length - 1 ? "text-right font-medium text-slate-100" : "text-slate-300"}`}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
